import { inject, injectable } from 'tsyringe';
import { type ISendCompanyRegistrationEmailClient } from 'interfaceAdapter/gatewaySchemas/ISendCompanyRegistrationEmailClient';
import { type ISendAgentRegistrationEmailClient } from 'interfaceAdapter/gatewaySchemas/ISendAgentRegistrationEmailClient';
import { type ISendBuyerRegistrationEmailClient } from 'interfaceAdapter/gatewaySchemas/ISendBuyerRegistrationEmailClient';
import { type ValuationEstimateData } from 'application/repositorySchemas/IValuationEstimateStorageRepository';
import { type IUserRegisterRepository } from 'application/repositorySchemas/IUserRegisterRepository';
import {
    type ICompanyUserRegistrationAuthClient,
    IPostResponse as CompanyUserResponse,
} from 'interfaceAdapter/gatewaySchemas/ICompanyUserRegistrationAuthClient';
import {
    type ICompanyUserRegistrationPasswordClient,
    IPostResponse as CompanyUserWithToken,
} from 'interfaceAdapter/gatewaySchemas/ICompanyUserRegistrationPasswordClient';
import {
    type IAgentUserRegistrationAuthClient,
    IPostResponse as AgentUserResponse,
} from 'interfaceAdapter/gatewaySchemas/IAgentUserRegistrationAuthClient';
import {
    type IAgentUserRegistrationPasswordClient,
    IPostResponse as AgentUserWithToken,
} from 'interfaceAdapter/gatewaySchemas/IAgentUserRegistrationPasswordClient';
import {
    type IBuyerUserRegistrationPasswordClient,
    IPostResponse as BuyerUserWithToken,
} from 'interfaceAdapter/gatewaySchemas/IBuyerUserRegistrationPasswordClient';
import {
    type IBuyerUserRegistrationAuthClient,
    IPostResponse as BuyerUserResponse,
} from 'interfaceAdapter/gatewaySchemas/IBuyerUserRegistrationAuthClient';
import {
    type ISupportUserRegistrationAuthClient,
    IPostResponse as SupportUserResponse,
} from 'interfaceAdapter/gatewaySchemas/ISupportUserRegistrationAuthClient';
import {
    type ISupportUserRegistrationPasswordClient,
    IPostResponse as SupportUserRegistrationPasswordResponse,
} from 'interfaceAdapter/gatewaySchemas/ISupportUserRegistrationPasswordClient';

export interface IUserRegister {
    readonly sendCompanyRegistrationEmailClient: ISendCompanyRegistrationEmailClient;
    readonly sendAgentRegistrationEmailClient: ISendAgentRegistrationEmailClient;
    readonly companyUserRegistrationAuthClient: ICompanyUserRegistrationAuthClient;
    readonly companyUserRegistrationPasswordClient: ICompanyUserRegistrationPasswordClient;
    readonly agentUserRegistrationAuthClient: IAgentUserRegistrationAuthClient;
    readonly agentUserRegistrationPasswordClient: IAgentUserRegistrationPasswordClient;
    readonly sendBuyerRegistrationEmailClient: ISendBuyerRegistrationEmailClient;
    readonly buyerUserRegistrationPasswordClient: IBuyerUserRegistrationPasswordClient;
    readonly buyerUserRegistrationAuthClient: IBuyerUserRegistrationAuthClient;
    readonly supportUserRegistrationAuthClient: ISupportUserRegistrationAuthClient;
    readonly supportUserRegistrationPasswordClient: ISupportUserRegistrationPasswordClient;
    sendCompanyRegistrationEmail(email: string): Promise<void>;
    urlAuth(urlId: string): Promise<CompanyUserResponse>;
    registrationPassword(urlId: string, password: string): Promise<CompanyUserWithToken>;
    sendAgentRegistrationEmail(email: string): Promise<void>;
    agentUrlAuth(urlId: string): Promise<AgentUserResponse>;
    agentRegistrationPassword(urlId: string, password: string): Promise<AgentUserWithToken>;
    sendBuyerRegistrationEmail(email: string): Promise<void>;
    buyerRegistrationPassword(urlId: string, password: string): Promise<BuyerUserWithToken>;
    buyerUrlAuth(urlId: string): Promise<BuyerUserResponse>;
    sendSupportRegistrationEmail(email: string): Promise<void>;
    supporterRegistrationPassword(urlId: string, password: string): Promise<SupportUserRegistrationPasswordResponse>;
    supporterUrlAuth(urlId: string): Promise<SupportUserResponse>;
}

@injectable()
export class UserRegisterRepository implements IUserRegisterRepository {
    constructor(
        @inject('ISendCompanyRegistrationEmailClient')
        public readonly sendCompanyRegistrationEmailClient: ISendCompanyRegistrationEmailClient,
        @inject('ISendAgentRegistrationEmailClient')
        public readonly sendAgentRegistrationEmailClient: ISendAgentRegistrationEmailClient,
        @inject('ISendBuyerRegistrationEmailClient')
        public readonly sendBuyerRegistrationEmailClient: ISendBuyerRegistrationEmailClient,
        @inject('ICompanyUserRegistrationAuthClient')
        public readonly companyUserRegistrationAuthClient: ICompanyUserRegistrationAuthClient,
        @inject('ICompanyUserRegistrationPasswordClient')
        public readonly companyUserRegistrationPasswordClient: ICompanyUserRegistrationPasswordClient,
        @inject('IAgentUserRegistrationAuthClient')
        public readonly agentUserRegistrationAuthClient: IAgentUserRegistrationAuthClient,
        @inject('IAgentUserRegistrationPasswordClient')
        public readonly agentUserRegistrationPasswordClient: IAgentUserRegistrationPasswordClient,
        @inject('IBuyerUserRegistrationPasswordClient')
        public readonly buyerUserRegistrationPasswordClient: IBuyerUserRegistrationPasswordClient,
        @inject('IBuyerUserRegistrationAuthClient')
        public readonly buyerUserRegistrationAuthClient: IBuyerUserRegistrationAuthClient,
        @inject('ISupportUserRegistrationAuthClient')
        public readonly supportUserRegistrationAuthClient: ISupportUserRegistrationAuthClient,
        @inject('ISupportUserRegistrationPasswordClient')
        public readonly supportUserRegistrationPasswordClient: ISupportUserRegistrationPasswordClient,
    ) {}

    public async sendCompanyRegistrationEmail(email: string, valuation_estimate_info?: ValuationEstimateData | null) {
        await this.sendCompanyRegistrationEmailClient.post({ email, valuation_estimate_info });
    }

    public async sendAgentRegistrationEmail(email: string) {
        await this.sendAgentRegistrationEmailClient.post({ email });
    }

    public async sendBuyerRegistrationEmail(email: string) {
        await this.sendBuyerRegistrationEmailClient.post({ email });
    }

    public async urlAuth(urlId: string, fromInvite?: boolean) {
        const { data, message } = await this.companyUserRegistrationAuthClient.post({ urlId, fromInvite });
        if (message) {
            throw new Error(message);
        }
        return data;
    }

    public async registrationPassword(urlId: string, password: string) {
        const { data, message } = await this.companyUserRegistrationPasswordClient.post({ urlId, password });
        if (message) {
            throw new Error(message);
        }
        return data;
    }

    public async agentUrlAuth(urlId: string, fromInvite?: boolean) {
        const { data, message } = await this.agentUserRegistrationAuthClient.post({ urlId, fromInvite });
        if (message) {
            throw new Error(message);
        }
        return data;
    }

    public async agentRegistrationPassword(urlId: string, password: string) {
        const { data, message } = await this.agentUserRegistrationPasswordClient.post({ urlId, password });
        if (message) {
            throw new Error(message);
        }
        return data;
    }

    public async buyerRegistrationPassword(urlId: string, password: string) {
        const { data, message } = await this.buyerUserRegistrationPasswordClient.post({ urlId, password });
        if (message) {
            throw new Error(message);
        }
        return data;
    }

    public async buyerUrlAuth(urlId: string, fromInvite?: boolean) {
        const { data, message } = await this.buyerUserRegistrationAuthClient.post({ urlId, fromInvite });
        if (message) {
            throw new Error(message);
        }
        return data;
    }

    public async supporterRegistrationPassword(urlId: string, password: string) {
        const { data, message } = await this.supportUserRegistrationPasswordClient.post({ urlId, password });
        if (message) {
            throw new Error(message);
        }
        return data;
    }

    public async supporterUrlAuth(urlId: string, fromInvite?: boolean) {
        const { data, message } = await this.supportUserRegistrationAuthClient.post({ urlId, fromInvite });
        if (message) {
            throw new Error(message);
        }
        return data;
    }
}
